import { useMemo } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import type { ProjectDocument } from '@/lib/db/rxdb/schemas/project'
import { useChildProjects, useRootProjects } from '@/lib/db/rxdb/hooks'
import useLoginStore from '@/store/LoginStore'
import { usePageLayout } from '@/hooks/usePageLayout'
import TaskSidebar from '@/components/app/sidebars/task'
import { ProjectSettingsForm } from '@/routes/features/projects/components/ProjectSettingsForm'

export const Route = createFileRoute(
  '/_AppWrapper/_AppLayout/$workspaceSlug/spaces/$spaceKey/projects/$projectKey/settings',
)({
  component: SpaceProjectSettingsPage,
})

export default function SpaceProjectSettingsPage() {
  const { workspaceSlug, spaceKey, projectKey: projectKeyParam } = Route.useParams()
  const projectKey = projectKeyParam.toUpperCase()
  const workspaceId = useLoginStore((state) => state.workspaceId)
  usePageLayout({ sidebarContent: <TaskSidebar /> })

  const { projects: rootProjects, isLoading: rootLoading } = useRootProjects()

  const space = useMemo<ProjectDocument | null>(() => {
    return rootProjects?.find((p) => {
      if (p.isDeleted) return false
      if (p.projectKey.toLowerCase() !== spaceKey.toLowerCase()) return false
      if (workspaceId && p.envId !== workspaceId) return false
      return true
    }) ?? null
  }, [rootProjects, spaceKey, workspaceId])

  const { projects: childProjects, isLoading: childLoading } = useChildProjects(space?.id)

  const project = useMemo<ProjectDocument | null>(() => {
    return (
      childProjects?.find((entry) => {
        if (entry.isDeleted) return false
        if (entry.projectKey !== projectKey) return false
        return true
      }) ?? null
    )
  }, [childProjects, projectKey])

  return (
    <ProjectSettingsForm
      project={project}
      isLoading={rootLoading || childLoading}
      backLinkTo="/$workspaceSlug/spaces/$spaceKey/projects/$projectKey"
      backLinkParams={{ workspaceSlug, spaceKey, projectKey: projectKeyParam }}
    />
  )
}
